/**
 * Support Store - Zustand store for managing support elements
 * Handles creation, deletion, and state management of support cubes
 */

import { create } from "zustand";
import {
  SupportConfig,
  SupportPosition,
  SupportType,
  DEFAULT_SUPPORT_CONFIG,
  DEFAULT_DOF_CONFIGS,
  SUPPORT_VISUAL_CONFIGS,
} from "../types/support";
import { v4 as uuidv4 } from "uuid";
import { addData } from "../services/centralized";
import {
  validateDuplicateSupportAssignments,
  validateSupportNodeReferences,
} from "../utils/structuralValidation";
import { useModelStore } from "./useModelStore";

interface SupportState {
  // State
  supports: SupportConfig[];
  selectedSupportIds: string[];
  isVisible: boolean; // Global visibility toggle for all supports

  // Actions
  addSupport: (
    nodeIds: string[],
    position: SupportPosition,
    type: SupportType
  ) => SupportConfig;
  removeSupport: (supportId: string) => void;
  // removeSupportsByNodeId: (nodeId: string) => void;
  updateSupport: (supportId: string, updates: Partial<SupportConfig>) => void;
  selectSupport: (supportId: string) => void;
  deselectSupport: (supportId: string) => void;
  clearSupportSelection: () => void;
  getSupportsByNodeId: (nodeId: string) => SupportConfig[];
  getSupportById: (supportId: string) => SupportConfig | undefined;
  getAllSupports: () => SupportConfig[];
  setSupports: (supports: SupportConfig[]) => void;

  // Utility functions
  hasSupport: (nodeId: string) => boolean;
  getSupportCount: () => number;
  clearAllSupports: () => void;

  // Visibility controls
  setVisible: (visible: boolean) => void;
  toggleVisible: () => void;
}

export const useSupportStore = create<SupportState>((set, get) => ({
  // Initial state
  supports: [],
  selectedSupportIds: [],
  isVisible: true, // Supports visible by default

  // Add a new support to a node
  addSupport: (
    nodeIds: string[],
    position: SupportPosition,
    type: SupportType
  ) => {
    const visualConfig = SUPPORT_VISUAL_CONFIGS[type];
    const dofConfig = DEFAULT_DOF_CONFIGS[type];

    const newSupport: SupportConfig = {
      id: uuidv4(),
      nodeIds,
      position,
      type,
      size: DEFAULT_SUPPORT_CONFIG.size,
      color: visualConfig.color,
      opacity: DEFAULT_SUPPORT_CONFIG.opacity,
      offset: DEFAULT_SUPPORT_CONFIG.offset,
      dof: { ...dofConfig }, // Copy DOF config for this support type
    };

    // Validate before adding support
    const state = get();

    // Import nodes from ModelStore for validation
    const nodes = useModelStore.getState().nodes;

    // Validate node references exist
    const nodeRefErrors = validateSupportNodeReferences(
      [newSupport],
      nodes.map((n) => n.id)
    );
    if (nodeRefErrors.length > 0) {
      throw new Error(nodeRefErrors[0].message);
    }

    // Validate no duplicate support assignments (one support per node)
    const tempSupports = [...state.supports, newSupport];
    const duplicateErrors = validateDuplicateSupportAssignments(tempSupports);
    if (duplicateErrors.length > 0) {
      throw new Error(duplicateErrors[0].message);
    }

    set((state) => ({
      supports: [...state.supports, newSupport],
    }));

    return newSupport;
  },

  // Remove a support by ID
  removeSupport: (supportId: string) => {
    set((state) => ({
      supports: state.supports.filter((support) => support.id !== supportId),
      selectedSupportIds: state.selectedSupportIds.filter(
        (id) => id !== supportId
      ),
    }));
  },

  // Remove all supports for a specific node
  // removeSupportsByNodeId: (nodeId: string) => {
  //   set((state) => {
  //     const supportsToRemove = state.supports
  //       .filter((support) => support.nodeId === nodeId)
  //       .map((support) => support.id);

  //     return {
  //       supports: state.supports.filter((support) => support.nodeId !== nodeId),
  //       selectedSupportIds: state.selectedSupportIds.filter(
  //         (id) => !supportsToRemove.includes(id)
  //       ),
  //     };
  //   });
  // },

  // Update a support's properties
  updateSupport: (supportId: string, updates: Partial<SupportConfig>) => {
    set((state) => ({
      supports: state.supports.map((support) =>
        support.id === supportId ? { ...support, ...updates } : support
      ),
    }));
  },

  // Select a support
  selectSupport: (supportId: string) => {
    set((state) => ({
      selectedSupportIds: [
        ...new Set([...state.selectedSupportIds, supportId]),
      ],
    }));
  },

  // Deselect a support
  deselectSupport: (supportId: string) => {
    set((state) => ({
      selectedSupportIds: state.selectedSupportIds.filter(
        (id) => id !== supportId
      ),
    }));
  },

  // Clear all support selections
  clearSupportSelection: () => {
    set({ selectedSupportIds: [] });
  },

  // Get all supports for a specific node
  getSupportsByNodeId: (nodeId: string) => {
    return get().supports.filter((s) => s.nodeIds.includes(nodeId));
  },

  // Get a support by ID
  getSupportById: (supportId: string) => {
    return get().supports.find((support) => support.id === supportId);
  },

  // Get all supports
  getAllSupports: () => {
    return get().supports;
  },

  // Set all supports (for import functionality)
  setSupports: (supports: SupportConfig[]) => {
    // Validate before setting (for Terminal/Import operations)

    // Import nodes from ModelStore for validation
    const nodes = useModelStore.getState().nodes;

    // Validate all node references exist
    const nodeRefErrors = validateSupportNodeReferences(
      supports,
      nodes.map((n) => n.id)
    );
    if (nodeRefErrors.length > 0) {
      throw new Error(nodeRefErrors[0].message);
    }

    // Validate no duplicate support assignments
    const duplicateErrors = validateDuplicateSupportAssignments(supports);
    if (duplicateErrors.length > 0) {
      throw new Error(duplicateErrors[0].message);
    }

    set({ supports });
  },

  // Check if a node has any supports
  hasSupport: (nodeId: string) => {
    return get().supports.some((s) => s.nodeIds.includes(nodeId));
  },

  // Get total support count
  getSupportCount: () => {
    return get().supports.length;
  },

  // Clear all supports
  clearAllSupports: () => {
    set({
      supports: [],
      selectedSupportIds: [],
    });
  },

  // Set visibility
  setVisible: (visible: boolean) => {
    set({ isVisible: visible });
    console.log("👁️ Supports visibility:", visible ? "ON" : "OFF");
  },

  // Toggle visibility
  toggleVisible: () => {
    set((state) => ({ isVisible: !state.isVisible }));
    console.log(
      "👁️ Supports visibility toggled:",
      get().isVisible ? "ON" : "OFF"
    );
  },
}));

// Subscribe to SupportStore and publish to centralized store
useSupportStore.subscribe((state) => {
  addData("supports", state.supports);
  console.log(
    "✅ [SupportStore→Centralized] Published supports:",
    state.supports.length
  );
});
